"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

/**
 * One item in a section's conceptual spine. Matching is serializable (no
 * functions) so a server layout can declare the spine and pass it to this
 * client component:
 *   - `exact`      — active when the pathname equals this exactly (the hub).
 *   - `startsWith` — active when the pathname starts with any of these. Among
 *                    all matching items the LONGEST prefix wins, so a specific
 *                    spoke (`/water/claims`) beats the catch-all figures item
 *                    (`/water/`) on its own page.
 */
export type SpineItem = {
  label: string;
  href: string;
  exact?: string;
  startsWith?: string[];
};

// Match strength: exact wins outright; otherwise the longest matching prefix.
// -1 means no match.
function matchLen(pathname: string, item: SpineItem): number {
  if (item.exact && pathname === item.exact) return Number.POSITIVE_INFINITY;
  let best = -1;
  for (const p of item.startsWith ?? []) {
    if (pathname === p || pathname.startsWith(p)) best = Math.max(best, p.length);
  }
  return best;
}

/**
 * Section spine — the conceptual sub-nav defined in `SECTION-IA.md`. A small
 * (≤5-item) set of zones a section is navigated by — Overview · The figures ·
 * Claims · <one extra> — NOT one tab per sub-page. Anchor items (`/water#figures`)
 * scroll within the hub and navigate-then-scroll from a spoke; page items
 * (`/water/claims`) cross-navigate.
 *
 * Shares the `cs-tabs`/`cs-tab` chrome with `SectionTabs` so it pins under the
 * site header consistently, but is route-matched (longest-prefix) rather than
 * segment-matched, and tints its active underline with the section's read hue
 * rather than the global signal red.
 */
export function SectionSpine({
  items,
  accent,
  ariaLabel,
  hiddenOnPrefixes,
}: {
  items: SpineItem[];
  accent: string;
  ariaLabel: string;
  /**
   * Route prefixes where this spine should NOT render — used by an index hub
   * whose nested sub-hubs own their own spine, so the section spine steps aside
   * inside them rather than stacking two strips (e.g. /transit suppresses its
   * spine inside /transit/green-line + /transit/free-fare-zone). Longest-prefix
   * not needed: a simple startsWith is enough.
   */
  hiddenOnPrefixes?: string[];
}) {
  const pathname = usePathname();

  // Suppress inside nested sub-hubs that own their own spine, so an index hub
  // doesn't stack two strips. Computed before the effect so a hidden spine
  // skips the header observer entirely (and re-attaches if it toggles visible).
  const hidden = !!hiddenOnPrefixes?.some(
    (p) => pathname === p || pathname.startsWith(p + "/"),
  );

  // Keep --site-header-h in sync with the real (sticky) header height so the
  // spine pins flush against its bottom edge instead of at a hardcoded 96px
  // (which leaves a gap once scrolled). Mirrors SectionTabs' header-sync; we
  // don't need its scroll-snap machinery since the spine has no sibling-tab
  // scroll choreography. Keyed on `hidden` so a hidden spine attaches no
  // observer, and one disconnects on a hidden ↔ visible navigation toggle.
  useEffect(() => {
    if (hidden) return;
    const header = document.querySelector("header");
    if (!header) return;
    const sync = () => {
      document.documentElement.style.setProperty(
        "--site-header-h",
        `${Math.ceil(header.getBoundingClientRect().height)}px`,
      );
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(header);
    return () => ro.disconnect();
  }, [hidden]);

  // rules-of-hooks: bail only after every hook has run.
  if (hidden) return null;

  let activeIdx = -1;
  let bestLen = -1;
  items.forEach((it, i) => {
    const len = matchLen(pathname, it);
    if (len > bestLen) {
      bestLen = len;
      activeIdx = i;
    }
  });

  return (
    <nav
      aria-label={ariaLabel}
      className="cs-tabs sticky z-40 border-y border-rule bg-bg/90 backdrop-blur-md"
      style={{ top: "var(--site-header-h, 96px)" }}
    >
      <div className="px-4 sm:px-8 overflow-x-auto">
        <ul className="flex items-stretch w-fit min-w-full sm:mx-auto">
          {items.map((it, i) => {
            const isActive = i === activeIdx;
            return (
              <li key={it.href}>
                <Link
                  href={it.href}
                  aria-current={isActive ? "page" : undefined}
                  data-active={isActive ? "true" : "false"}
                  className="cs-tab group relative flex items-baseline gap-2 px-3 sm:px-4 py-4 sm:py-5 transition-colors duration-150 hover:bg-bg-2 border-r border-rule last:border-r-0"
                >
                  <span className="cs-mono text-[12px] sm:text-[13px] text-ink-3 group-data-[active=true]:text-ink leading-none whitespace-nowrap">
                    {it.label}
                  </span>
                  <span
                    aria-hidden
                    className="pointer-events-none absolute left-0 right-0 bottom-[-1px] h-[3px] origin-left scale-x-0 group-data-[active=true]:scale-x-100 transition-transform duration-300"
                    style={{ background: accent }}
                  />
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
